import { Button, Drawer, message, Modal, Space, Typography, Input, Select, Divider } from "antd"
import { doc, writeBatch, arrayUnion, Timestamp } from "firebase/firestore"
import React, { useState, useMemo } from "react"
import { db } from "../../firebase/config"
import { ShoppingCartOutlined, SendOutlined } from "@ant-design/icons"

const { Text } = Typography
const { TextArea } = Input

// All possible production statuses
const allProductionStatuses = [
  { value: 2, label: "PAPER MANAGER" },
  { value: 3, label: "Arsoma" },
  { value: 3.5, label: "Tape" },
  { value: 4, label: "Viva" },
  { value: 5, label: "Winder" },
]

export default function PaperManagerOrderOptions({ order, user, ticket, setSelectedTicket }) {
  const [open, setOpen] = useState(false)
  const [isDeletionModalVisible, setIsDeletionModalVisible] = useState(false)
  const [isProductionModalVisible, setIsProductionModalVisible] = useState(false)
  const [reason, setReason] = useState("")
  const [completionComment, setCompletionComment] = useState("")
  const [productionStatus, setProductionStatus] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // --- NEW: Memoized logic to filter statuses based on the order's machine ---
  const filteredProductionStatuses = useMemo(() => {
    const machine = order?.selectedProduct?.machine
    const baseOptions = [
      { value: 2, label: "PAPER MANAGER" },
      { value: 5, label: "Winder" },
    ]

    switch (machine) {
      case "Viva":
        return [...baseOptions, { value: 4, label: "Viva" }]
      case "Arsoma":
        return [...baseOptions, { value: 3, label: "Arsoma" }]
      case "Tape":
        return [...baseOptions, { value: 3.5, label: "Tape" }]
      default:
        // If machine is not specified or doesn't match, return all statuses
        return allProductionStatuses
    }
  }, [order?.selectedProduct?.machine])

  const showDrawer = () => setOpen(true)
  const onClose = () => setOpen(false)

  const showDeletionModal = () => setIsDeletionModalVisible(true)
  const showProductionModal = () => setIsProductionModalVisible(true)

  const handleModalCancel = () => {
    setIsDeletionModalVisible(false)
    setIsProductionModalVisible(false)
    setReason("")
    setCompletionComment("")
    setProductionStatus(null)
  }

  const handleDeletionRequest = async () => {
    if (!reason.trim()) {
      return message.error("A reason for the deletion request is required.")
    }
    if (!order || !ticket || !user) {
      return message.error("Missing required data to process this request.")
    }
    if (!order.uid || !order.user) {
      message.error(
        "Cannot process request: The order is missing the sales manager's details (ID or Email)."
      )
      return
    }

    setSubmitting(true)
    const batch = writeBatch(db)
    const orderRef = doc(db, "orders", order.id)
    batch.update(orderRef, { orderStatus: 997 })

    const ticketRef = doc(db, "tickets", ticket.id)
    const requestMessage = {
      id: `${Timestamp.now().toMillis()}-${user.uid}`,
      text: `Deletion Requested: "${reason}"`,
      senderId: user.uid,
      senderEmail: user.email,
      timestamp: Timestamp.now(),
      type: "deletion_request",
      response: null,
    }

    batch.update(ticketRef, {
      status: "awaiting-response",
      assignedToId: arrayUnion(order.uid),
      assignedTo: arrayUnion(order.user),
      messages: arrayUnion(requestMessage),
    })

    try {
      await batch.commit()
      message.success(`Deletion request for ${order.orderNumber} has been sent.`)
      handleModalCancel()
      onClose()
    } catch (error) {
      console.error("Error sending deletion request:", error)
      message.error("Failed to send the deletion request.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleSendToProduction = async () => {
    if (!productionStatus) {
      return message.error("You must select a production status.")
    }
    if (!completionComment.trim()) {
      return message.error("A completion comment is required.")
    }
    if (!order || !ticket || !user) {
      return message.error("Missing required data to process this request.")
    }

    setSubmitting(true)
    const batch = writeBatch(db)

    const orderRef = doc(db, "orders", order.id)
    batch.update(orderRef, { orderStatus: productionStatus })

    const ticketRef = doc(db, "tickets", ticket.id)
    const closedTicketRef = doc(db, "closedTickets", ticket.id)

    const systemMessage = {
      id: `${Timestamp.now().toMillis()}-system`,
      text: `Ticket closed by ${user.email}. Order sent to production stage: ${
        allProductionStatuses.find((s) => s.value === productionStatus)?.label
      }. Comment: "${completionComment}"`,
      senderId: "system",
      senderEmail: "System",
      timestamp: Timestamp.now(),
      type: "system",
    }

    const closedTicketData = {
      ...ticket,
      messages: [...(ticket.messages || []), systemMessage],
      status: "closed",
      closedAt: Timestamp.now(),
    }

    batch.set(closedTicketRef, closedTicketData)
    batch.delete(ticketRef)

    try {
      await batch.commit()
      message.success(`Order sent to production and ticket has been closed.`)
      handleModalCancel()
      onClose()
    } catch (error) {
      console.error("Error sending order to production:", error)
      message.error("Failed to update the order and close the ticket.")
    } finally {
      setSubmitting(false)
      setSelectedTicket(null)
    }
  }

  return (
    <>
      <Button icon={<ShoppingCartOutlined />} type="primary" onClick={showDrawer}>
        Manage Order
      </Button>
      <Drawer
        title={`Options for Order: ${order.orderNumber}`}
        closable={true}
        onClose={onClose}
        open={open}
        height={300}
        placement="left">
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>
            Send this order back to a specific production stage. This will close the current ticket.
          </Text>
          <Button type="primary" block onClick={showProductionModal} icon={<SendOutlined />}>
            Send to Production
          </Button>

          <Divider />

          <Text>
            Request deletion from the responsible Sales Manager. This action cannot be undone.
          </Text>
          <Button type="primary" danger block onClick={showDeletionModal}>
            Request Order Deletion
          </Button>
        </Space>
      </Drawer>

      <Modal
        title="Deletion Request Reason"
        open={isDeletionModalVisible}
        onOk={handleDeletionRequest}
        onCancel={handleModalCancel}
        confirmLoading={submitting}
        okText="Send Request">
        <Typography.Text>
          Please provide a reason why this order should be deleted. This will be sent to the Sales
          Manager.
        </Typography.Text>
        <TextArea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Client cancelled, duplicate order, etc."
          style={{ marginTop: "15px" }}
        />
      </Modal>

      <Modal
        title="Send Order to Production"
        open={isProductionModalVisible}
        onOk={handleSendToProduction}
        onCancel={handleModalCancel}
        confirmLoading={submitting}
        okText="Confirm & Complete Ticket">
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>Select the next production stage for this order.</Text>
          <Select
            style={{ width: "100%" }}
            placeholder="Choose a status"
            onChange={(value) => setProductionStatus(value)}
            // Use the dynamically filtered list of options here
            options={filteredProductionStatuses}
          />
          <Text style={{ marginTop: "10px" }}>Add a final comment for closing this ticket.</Text>
          <TextArea
            rows={4}
            value={completionComment}
            onChange={(e) => setCompletionComment(e.target.value)}
            placeholder="e.g., Issue resolved, materials checked, etc."
          />
        </Space>
      </Modal>
    </>
  )
}
